/**
 * <scroll-to-reviews> — clickable rating wrapper that reveals the reviews tab.
 *
 * Why: the rating block in `sections/main-product.liquid` exposes a
 * "Make rating clickable" toggle. When enabled, clicking the stars must:
 *   1. Open the enclosing <details> (Product details "collapsible" tab style).
 *   2. Activate the matching <tabbed-content> tab ("horizontal_tabs" style).
 *   3. Smooth-scroll the reviews tab into view at its title, not deep inside
 *      the embedded widget.
 *
 * Target resolution is vendor-neutral and zero-config: the element looks for
 * known reviews-widget wrapper IDs emitted by `sections/product-details.liquid`
 * (Judge.me, then Shopify Product Reviews). Add new IDs to TARGET_IDS to
 * support additional reviews apps.
 *
 * Scroll anchor: rather than scrolling to the widget itself, the element walks
 * up to the enclosing <details> (collapsible) or <tabbed-content> (tabs) so
 * the buyer lands on the tab heading. Falls back to the widget element when
 * neither ancestor is present.
 *
 * Architecture: extends `theme.BaseElement` so the click listener is auto-
 * cleaned via AbortController on `disconnectedCallback`. This matters in the
 * Theme Editor where sections reorder/reload without a full page navigation
 * (see ADR-0001-base-custom-element.md).
 *
 * Doc: docs/decisions/ADR-0001-base-custom-element.md
 */

if (!customElements.get('scroll-to-reviews')) {
  // Ordered candidates: Judge.me first (current default reviews app), then
  // the SPR-compatible wrapper that `sections/product-details.liquid` emits
  // when `custom_reviews` is blank. Add new IDs here if a future reviews app
  // ships a different wrapper ID.
  const TARGET_IDS = ['judgeme_product_reviews', 'shopify-product-reviews'];

  class ScrollToReviews extends theme.BaseElement {
    connectedCallback() {
      this.link = this.querySelector('a');
      if (!this.link) return;
      this.on(this.link, 'click', this.handleClick.bind(this));
    }

    /**
     * @param {MouseEvent} evt
     */
    handleClick(evt) {
      const target = this.resolveTarget();
      // No reviews widget on page: let the browser follow the anchor href so
      // the user is not silently stranded.
      if (!target) return;

      evt.preventDefault();
      this.openEnclosingDetails(target);
      this.activateEnclosingTab(target);

      const scrollAnchor = this.resolveScrollAnchor(target);
      // requestAnimationFrame: opening <details> and clicking a tab trigger
      // layout reflow. Deferring scroll one frame lets the final geometry
      // settle so `scrollIntoView` lands on the correct position.
      requestAnimationFrame(() => {
        scrollAnchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    /**
     * @returns {HTMLElement|null}
     */
    resolveTarget() {
      for (const id of TARGET_IDS) {
        const el = document.getElementById(id);
        if (el) return el;
      }
      return null;
    }

    /**
     * Find the outermost wrapper that represents the "tab" boundary so the
     * scroll lands on the title row, not inside the widget.
     *
     * @param {HTMLElement} target
     * @returns {HTMLElement}
     */
    resolveScrollAnchor(target) {
      return target.closest('details-disclosure') || target.closest('details') || target.closest('tabbed-content') || target;
    }

    /**
     * @param {HTMLElement} target
     */
    openEnclosingDetails(target) {
      const details = target.closest('details');
      if (details && !details.open) details.open = true;
    }

    /**
     * @param {HTMLElement} target
     */
    activateEnclosingTab(target) {
      const panel = target.closest('[role="tabpanel"]');
      if (!panel) return;
      const tabId = panel.getAttribute('aria-labelledby');
      if (!tabId) return;
      const tab = document.getElementById(tabId);
      if (tab && tab.getAttribute('aria-selected') !== 'true') tab.click();
    }
  }

  customElements.define('scroll-to-reviews', ScrollToReviews);
}
